import axios, { AxiosError, InternalAxiosRequestConfig, isCancel } from 'axios'
import { logout } from './authed/auth.actions'
import { customHistory } from './global/history'
import { AppDispatch, store } from './redux/store'

// TODO: what is the real shape of the data object returned by the API for an error?
export interface IApiClaravineErrorData {
  message?: string
  error?: string
  errors?: string[]
  code?: string
  path?: string
  status?: number
}

let refreshTokenEnabled = true
let redirect = false
let TOKEN_REFRESH_PROMISE: Promise<void> | null = null

export const api = axios.create({
  headers: {
    // eslint-disable-next-line lingui/no-unlocalized-strings
    'X-Requested-With': 'XMLHttpRequest',
  },
  withCredentials: true,
})

api.interceptors.response.use(response => response, catchAllResponseFailures)
api.interceptors.request.use(modifyAllRequestConfigs)

async function catchAllResponseFailures(error: unknown) {
  if (isCancel(error) || !(error instanceof AxiosError)) {
    throw error
  }

  const originalRequest = error.config

  if (originalRequest && refreshTokenEnabled) {
    const errorStatusIsUnauthorized = error.response?.status === 401

    const requestHasNotBeenTriedAgain = !originalRequest.headers['X-Retry']
    if (
      errorStatusIsUnauthorized &&
      requestHasNotBeenTriedAgain &&
      !window.location.pathname.includes('auth')
    ) {
      await refreshTokenMemo()

      originalRequest.headers['X-Retry'] = 'true'
      const refreshed = await modifyAllRequestConfigs(originalRequest)

      return axios(refreshed)
    }
  }

  throw error
}

async function modifyAllRequestConfigs(config: InternalAxiosRequestConfig) {
  // delay all requests until the refresh promise finishes
  if (TOKEN_REFRESH_PROMISE) {
    await TOKEN_REFRESH_PROMISE
  }

  const session_toe = localStorage.getItem('session_toe')
  if (session_toe !== null) {
    config.headers.TOE = session_toe
  }

  return config
}

const refreshTokenMemo = async () => {
  // if a refresh is already in progress, just rely on that one...
  // if another is fired, it will fail if the first succeeds, causing an invalid logout
  if (TOKEN_REFRESH_PROMISE) {
    return TOKEN_REFRESH_PROMISE
  }

  TOKEN_REFRESH_PROMISE = refreshToken()

  try {
    await TOKEN_REFRESH_PROMISE
  } finally {
    TOKEN_REFRESH_PROMISE = null
  }
}

const refreshToken = async () => {
  try {
    await axios.post('/login/refresh', null, { withCredentials: true })
  } catch (error: unknown) {
    if (!(error instanceof AxiosError)) {
      return
    }

    const errorIsOauthInvalid =
      error.response?.status === 403 ||
      error.response?.status === 419 ||
      error.response?.status === 500

    if (!errorIsOauthInvalid) {
      return
    }

    // update the local redux state to indicate that we're no longer logged in
    void (store.dispatch as AppDispatch)(logout()).finally(() => {
      // once state is cleared, redirect where we need to go
      const queryParams = new URLSearchParams(location.search)
      const forceRegularLogin = queryParams.get('login') === 'system'

      if (redirect && !forceRegularLogin) {
        window.location.replace('/login') // re-initiate SSO flow
      } else {
        customHistory.push('/auth/login?login=system', { referrer: customHistory.location })
      }
    })
  }
}

export const enableRefreshToken = (enabled: boolean) => {
  refreshTokenEnabled = enabled
}

export const redirectOnLogout = (enabled: boolean) => {
  redirect = enabled
}
